import { applySnapshot, getSnapshot, types } from "mobx-state-tree";
import { DATE_TIME_FORMAT } from "modules/common/constants";
import { BankDetails } from "modules/common/models/bank-details";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import moment from "moment";
import { cleaners } from "./bank-row";

export const ReturnRow = types
  .model({
    id: types.string,
    sum: types.number,
    date: types.string,
    description: types.string,
    type: types.string,
    number: types.string,
    recipient: types.string,
    recipientBankDetails: BankDetails,
    payer: types.string,
    payerBankDetails: BankDetails,
    operation: types.maybeNull(types.string),
    uniqueHash: types.string,
    processed: types.boolean,
    incoimng: types.boolean,
    payload: types.maybeNull(types.string),
  })
  .views((self) => ({
    get dateAsDate() {
      return moment(self.date, DATE_TIME_FORMAT);
    },
    get cleanPayer() {
      let result = self.payer;
      cleaners.forEach(([pattern, replacement]) => (result = result.replace(pattern, replacement)));

      return result;
    },

    get cleanRecipient() {
      let result = self.recipient;
      cleaners.forEach(([pattern, replacement]) => (result = result.replace(pattern, replacement)));

      return result;
    },
  }))
  .named("ReturnRow");

export const ReturnListStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      loading: types.boolean,
      rows: types.array(ReturnRow),
      hasMore: types.boolean,
    })
  )
  .volatile(() => ({
    // идёт загрузка следующей страницы — см. loadNext
    loadingNext: false,
  }))
  .actions((self) => ({
    setData(data: ReturnRowSnapshotType[], append = true) {
      if (data.length) {
        if (append) {
          applySnapshot(self.rows, [...getSnapshot(self.rows), ...data]);
        } else {
          applySnapshot(self.rows, [...data, ...getSnapshot(self.rows)]);
        }
      }
    },
  }))
  .actions((self) => ({
    load: flow(function* () {
      try {
        self.loading = true;
        const fromId = "";
        const data: any[] = yield self.transport.get<any>(apiUrls.spendings.import.getReturn, {
          params: { fromId, limit: LIMIT },
        });
        applySnapshot(self.rows, data);
        self.hasMore = data.length === LIMIT;
        self.loading = false;
        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),

    loadNext: flow(function* () {
      // Пока страница грузится, повторный вызов ушёл бы с тем же fromId и дописал её
      // в список второй раз: так бывает при быстрой прокрутке, а в dev — на каждом
      // открытии, потому что StrictMode монтирует компонент дважды
      if (self.loadingNext) {
        return false;
      }

      self.loadingNext = true;
      try {
        // const fromId = self.rows.length ? self.rows[self.rows.length - 1].id : "";

        let fromId = "";
        if (self.rows.length) {
          const lastRow = self.rows[self.rows.length - 1];
          // Предполагается, что lastRow.date - это Date объект
          // Преобразуем дату в строку в ISO формате и объединяем с id через разделитель
          fromId = `${lastRow.date}_${lastRow.id}`;
        }

        const data: any[] = yield self.transport.get<any>(apiUrls.spendings.import.getReturn, {
          params: { fromId, limit: LIMIT },
        });
        self.hasMore = data.length === LIMIT;
        self.setData(data);
        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      } finally {
        self.loadingNext = false;
      }
    }),
    returnPayment: flow(function* (id: string) {
      try {
        const data: boolean[] = yield self.transport.post<any>(apiUrls.spendings.import.returnId, {
          id,
        });
        if (data) {
          const filteredRows = getSnapshot(self.rows).filter((row) => row.id !== id);
          applySnapshot(self.rows, filteredRows);
        }
        self.notify.success("Платеж успешно возвращен");
        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),
  }))
  .named("ReturnListStore");

const LIMIT = 12;
export type ReturnRowType = typeof ReturnRow.Type;
export type ReturnRowSnapshotType = typeof ReturnRow.SnapshotType;
export type ReturnListStoreType = typeof ReturnListStore.Type;
